import {
  useQuery,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";
import { useAuthStore } from "@/store/auth.store";
import { addressService } from "@/services/address.service";
import type {
  CreateAddressPayload,
  UpdateAddressPayload,
  AddressAutocompleteParams,
  ListAddressesParams,
} from "@/types";

const ADDRESSES_KEY = "addresses";

// ── List ──────────────────────────────────────────────────────────────────────

export function useAddresses(params: ListAddressesParams = {}) {
  const user = useAuthStore((s) => s.user);
  return useQuery({
    queryKey: [ADDRESSES_KEY, "list", params],
    queryFn: () => addressService.list(params),
    enabled: !!user,
  });
}

// ── Favorites ─────────────────────────────────────────────────────────────────

export function useAddressFavorites() {
  const user = useAuthStore((s) => s.user);
  return useQuery({
    queryKey: [ADDRESSES_KEY, "favorites"],
    queryFn: () => addressService.listFavorites(),
    enabled: !!user,
  });
}

// ── Get one ───────────────────────────────────────────────────────────────────

export function useAddress(ulid: string) {
  const user = useAuthStore((s) => s.user);
  return useQuery({
    queryKey: [ADDRESSES_KEY, ulid],
    queryFn: () => addressService.getById(ulid),
    enabled: !!user && !!ulid,
  });
}

// ── Autocomplete ──────────────────────────────────────────────────────────────

export function useAddressAutocomplete(params: AddressAutocompleteParams) {
  const user = useAuthStore((s) => s.user);
  return useQuery({
    // Photon pour tout le monde : la route backend `/addresses/autocomplete`
    // n'accepte que `mapbox | google` comme fournisseur, donc y passer pour les
    // personnes connectées ferait cohabiter deux moteurs — des résultats
    // différents selon qu'on est connecté ou non, pour la même recherche.
    queryKey: [ADDRESSES_KEY, "autocomplete", { params }],
    // L'Algérie par défaut : deux écrans seulement passaient `country`, si
    // bien qu'ailleurs une recherche « Hydra » pouvait remonter des résultats
    // d'un autre pays avant les algériens.
    queryFn: () => addressService.photonAutocomplete({ country: "dz", ...params }),
    enabled: params.query.length >= 2,
    staleTime: 30_000,
  });
}

// ── Create ────────────────────────────────────────────────────────────────────

export function useCreateAddress() {
  const user = useAuthStore((s) => s.user);
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateAddressPayload) =>
      addressService.create(payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [ADDRESSES_KEY] });
    },
  });
}

// ── Update ────────────────────────────────────────────────────────────────────

export function useUpdateAddress() {
  const user = useAuthStore((s) => s.user);
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ ulid, payload }: { ulid: string; payload: UpdateAddressPayload }) =>
      addressService.update(ulid, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [ADDRESSES_KEY] });
    },
  });
}

// ── Delete ────────────────────────────────────────────────────────────────────

export function useDeleteAddress() {
  const user = useAuthStore((s) => s.user);
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (ulid: string) => addressService.delete(ulid),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [ADDRESSES_KEY] });
    },
  });
}

// ── Toggle favorite ───────────────────────────────────────────────────────────

export function useToggleFavorite() {
  const user = useAuthStore((s) => s.user);
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ ulid, isFavorite }: { ulid: string; isFavorite: boolean }) =>
      isFavorite ? addressService.removeFavorite(ulid) : addressService.addFavorite(ulid),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [ADDRESSES_KEY] });
    },
  });
}
